import React, { memo, useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { areEqual } from 'Utils/equalityChecks';

import { ManageTimecardTypesModal } from 'Components/TimeTracking';

import {
  listTimecardTypes,
  createTimecardType,
  updateTimecardType,
  deleteTimecardType,
  listCompanyTimecardRates,
  setCompanyTimecardTypeRate,
  updateCompanyTimecardTypeRate,
  setCompanyDefaultLaborRate,
} from 'Containers/TimeTracking/actions';

import {
  timecardTypesSelector,
  createTimecardTypeErrorsSelector,
  updateTimecardTypeErrorsSelector,
  companyTimecardRatesSelector,
  companyDefaultLaborRateSelector,
} from 'Containers/TimeTracking/selectors';

import { firstCompanyIdSelector } from 'Containers/Projects/selectors';

import { useTimeTrackingFunctions } from 'Context/TimeTracking';

const ManageTimecardTypesModalContainer = () => {
  const dispatch = useDispatch();

  const [newTypeName, setNewTypeName] = useState('');
  const [editingTypeId, setEditingTypeId] = useState<number | null>(null);
  const [editingTypeName, setEditingTypeName] = useState('');
  const [editingTypeRate, setEditingTypeRate] = useState('');
  const [defaultLaborRate, setDefaultLaborRate] = useState<number | null>(null);

  const { isManageTypesModalOpen, setIsManageTypesModalOpen }: any = useTimeTrackingFunctions();

  const firstCompanyId = useSelector(firstCompanyIdSelector, areEqual);
  const timecardTypes = useSelector(timecardTypesSelector, areEqual);
  const companyRates = useSelector(companyTimecardRatesSelector, areEqual);
  const storedDefaultRate = useSelector(companyDefaultLaborRateSelector, areEqual);
  const createErrors = useSelector(createTimecardTypeErrorsSelector, areEqual);
  const updateErrors = useSelector(updateTimecardTypeErrorsSelector, areEqual);

  useEffect(() => {
    if (isManageTypesModalOpen) {
      dispatch(listTimecardTypes());
      if (firstCompanyId) {
        dispatch(listCompanyTimecardRates(firstCompanyId));
      }
    }
  }, [isManageTypesModalOpen, firstCompanyId]);

  useEffect(() => {
    if (storedDefaultRate !== null) {
      setDefaultLaborRate(storedDefaultRate);
    }
  }, [storedDefaultRate]);

  const closeModal = useCallback(() => {
    setIsManageTypesModalOpen(false);
    setNewTypeName('');
    setEditingTypeId(null);
    setEditingTypeName('');
    setEditingTypeRate('');
  }, []);

  const onCreateType = useCallback(() => {
    if (newTypeName.trim()) {
      dispatch(
        createTimecardType(newTypeName.trim(), () => {
          setNewTypeName('');
        })
      );
    }
  }, [newTypeName]);

  const onStartEdit = useCallback((id: number, name: string) => {
    setEditingTypeId(id);
    setEditingTypeName(name);
    const rate = companyRates.find((r: any) => r.timecard_type_id === id);
    setEditingTypeRate(rate ? String(rate.rate) : '');
  }, [companyRates]);

  const onCancelEdit = useCallback(() => {
    setEditingTypeId(null);
    setEditingTypeName('');
    setEditingTypeRate('');
  }, []);

  const onDeleteType = useCallback((id: number) => {
    dispatch(deleteTimecardType(id));
  }, []);

  const onNewTypeNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setNewTypeName(e.target.value);
  }, []);

  const onEditTypeNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingTypeName(e.target.value);
  }, []);

  const onEditTypeRateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingTypeRate(e.target.value);
  }, []);

  const onDefaultRateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDefaultLaborRate(value ? parseFloat(value) : null);
  }, []);

  const onSaveDefaultRate = useCallback(() => {
    if (firstCompanyId && defaultLaborRate !== null) {
      dispatch(
        setCompanyDefaultLaborRate(firstCompanyId, defaultLaborRate, () => {
          // Rate saved successfully
        })
      );
    }
  }, [firstCompanyId, defaultLaborRate]);

  // pending: project-level rate UI - these actions will be used when that UI is built
  // setProjectTimecardTypeRate, updateProjectTimecardTypeRate, deleteProjectTimecardTypeRate, listProjectTimecardRates

  const onSaveBoth = useCallback(
    (typeId: number) => {
      if (!editingTypeName.trim()) return;

      // Save the rate independently of the rename — setting a rate must not depend on a
      // successful name update (renaming a shared type can no-op or fail, which would
      // otherwise silently drop the rate).
      const saveRate = () => {
        if (firstCompanyId && editingTypeRate) {
          const rate = parseFloat(editingTypeRate);
          if (!isNaN(rate)) {
            const existingRate = companyRates.find((r: any) => r.timecard_type_id === typeId);
            if (existingRate) {
              dispatch(updateCompanyTimecardTypeRate(firstCompanyId, typeId, rate));
            } else {
              dispatch(setCompanyTimecardTypeRate(firstCompanyId, typeId, rate));
            }
          }
        }
      };

      const clearEdit = () => {
        setEditingTypeId(null);
        setEditingTypeName('');
        setEditingTypeRate('');
      };

      const original = timecardTypes.find((t: any) => t.id === typeId);
      const nameChanged = original && original.name !== editingTypeName.trim();

      if (nameChanged) {
        dispatch(
          updateTimecardType(typeId, editingTypeName.trim(), () => {
            saveRate();
            clearEdit();
          })
        );
      } else {
        saveRate();
        clearEdit();
      }
    },
    [editingTypeName, editingTypeRate, firstCompanyId, companyRates, timecardTypes]
  );

  return (
    <ManageTimecardTypesModal
      isOpen={isManageTypesModalOpen}
      timecardTypes={timecardTypes}
      companyRates={companyRates}
      defaultLaborRate={defaultLaborRate}
      newTypeName={newTypeName}
      editingTypeId={editingTypeId}
      editingTypeName={editingTypeName}
      editingTypeRate={editingTypeRate}
      createErrors={createErrors}
      updateErrors={updateErrors}
      onClose={closeModal}
      onCreateType={onCreateType}
      onStartEdit={onStartEdit}
      onCancelEdit={onCancelEdit}
      onDeleteType={onDeleteType}
      onNewTypeNameChange={onNewTypeNameChange}
      onEditTypeNameChange={onEditTypeNameChange}
      onEditTypeRateChange={onEditTypeRateChange}
      onDefaultRateChange={onDefaultRateChange}
      onSaveDefaultRate={onSaveDefaultRate}
      onSaveBoth={onSaveBoth}
    />
  );
};

const ManageTimecardTypesModalContainerMemo = memo(ManageTimecardTypesModalContainer, areEqual);

export { ManageTimecardTypesModalContainerMemo as ManageTimecardTypesModal };
